
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MFOCode } from "@/hooks/use-mfo-codes";

interface EditMFOCodeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: Partial<MFOCode>) => void;
  mfo: MFOCode | null;
}

const EditMFOCodeModal: React.FC<EditMFOCodeModalProps> = ({ 
  isOpen, 
  onOpenChange, 
  onSave,
  mfo
}) => {
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or mfo changes
  useEffect(() => {
    if (mfo && isOpen) {
      setCode(mfo.code || "");
      setTitle(mfo.title || "");
      setBudget(mfo.budget?.toString() || "0");
    } else {
      setCode("");
      setTitle("");
      setBudget("");
    }
  }, [mfo, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfo) return;
    
    setIsSubmitting(true);
    try {
      await onSave({
        id: mfo.id,
        code,
        title,
        budget: parseFloat(budget) || 0
      });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen && !!mfo} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit MFO Code</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter the MFO code"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter the MFO title"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget">Budget</Label>
              <Input
                id="budget"
                type="number"
                min="0"
                step="0.01"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="Enter the budget amount"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              type="button"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMFOCodeModal;
